import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, ChevronLeft, ChevronRight, Shuffle } from 'lucide-react'
import { JIZURA_INITIAL_SEED, JIZURA_STYLES, useJizura } from './JizuraSettings'

const chipClass = (isDaylight: boolean) =>
  `h-7 rounded-lg px-2 text-[11px] font-medium tracking-[0.04em] transition-all ${isDaylight ? 'bg-white shadow-sm hover:bg-white/90' : 'bg-white/15 shadow-sm hover:bg-white/25'}`

export function JizuraPalettePicker({ isDaylight }: { isDaylight: boolean }) {
  const { settings, update, recordingBusy } = useJizura()
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ left: number; top: number; width: number; maxHeight: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const selectedLabel = JIZURA_STYLES.find(([value]) => value === settings.style)?.[1] ?? JIZURA_STYLES[0][1]

  const placeMenu = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const width = Math.min(220, window.innerWidth - 24)
    const left = Math.max(8, Math.min(window.innerWidth - width - 8, rect.right - width))
    const spaceBelow = window.innerHeight - rect.bottom - 14
    const spaceAbove = rect.top - 14
    const below = spaceBelow >= Math.min(260, window.innerHeight - 24) || spaceBelow >= spaceAbove
    const maxHeight = Math.min(340, Math.max(0, below ? spaceBelow : spaceAbove))
    const top = below ? rect.bottom + 6 : rect.top - maxHeight - 6
    setPosition({ left, top, width, maxHeight })
  }, [])

  useEffect(() => {
    if (!open) return
    placeMenu()
    const onPointerDown = (event: PointerEvent) => {
      if (!triggerRef.current?.contains(event.target as Node) && !menuRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', placeMenu)
    window.addEventListener('scroll', placeMenu, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', placeMenu)
      window.removeEventListener('scroll', placeMenu, true)
    }
  }, [open, placeMenu])

  return (
    <>
      <button ref={triggerRef} type="button" disabled={recordingBusy} onClick={() => setOpen(value => !value)}
        className={`${chipClass(isDaylight)} flex max-w-[7.25rem] items-center gap-1 disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label="配色风格" title={`配色风格：${selectedLabel}`} aria-haspopup="listbox" aria-expanded={open}>
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown size={10} className={`shrink-0 opacity-55 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && position && createPortal(
        <div ref={menuRef} role="listbox" aria-label="配色风格" className="jizura-palette-menu" style={position}>
          <div className="jizura-palette-heading">选择配色</div>
          {JIZURA_STYLES.map(([value, label]) => (
            <button key={value} type="button" role="option" aria-selected={settings.style === value}
              className={`jizura-palette-option ${settings.style === value ? 'is-selected' : ''}`}
              onClick={() => { update({ style: value }); setOpen(false); triggerRef.current?.focus() }}>
              <span className="jizura-palette-swatch" data-style={value} />
              <span>{label}</span>
              {settings.style === value && <Check size={14} className="ml-auto text-rose-300" />}
            </button>
          ))}
        </div>, document.body,
      )}
    </>
  )
}

export function JizuraVersionRow({ isDaylight }: { isDaylight: boolean }) {
  const { settings, update, recordingBusy } = useJizura()
  const version = Math.max(1, settings.seed - JIZURA_INITIAL_SEED + 1)
  const hover = isDaylight ? 'hover:bg-black/[0.06]' : 'hover:bg-white/[0.08]'
  const step = (direction: -1 | 1) => update({ seed: (settings.seed + direction) >>> 0 })

  return (
    <div className="flex items-center gap-0.5">
      <button type="button" onClick={() => step(-1)} disabled={recordingBusy || version <= 1}
        className={`flex h-8 w-6 shrink-0 items-center justify-center rounded-lg opacity-40 transition-colors ${hover} hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-20`}
        aria-label="上一版动画"><ChevronLeft size={14} /></button>
      <div className="flex h-8 min-w-0 flex-1 items-center gap-2 px-2">
        <Shuffle size={14} className="shrink-0 opacity-55" />
        <span className="truncate text-[13px] font-medium tracking-[0.04em]">动画版本</span>
      </div>
      <button type="button" onClick={() => step(1)} disabled={recordingBusy}
        className={`${chipClass(isDaylight)} shrink-0 disabled:cursor-not-allowed disabled:opacity-40`}
        aria-label="换一版动画" title="换一版动画">第 {version} 版</button>
      <button type="button" onClick={() => step(1)} disabled={recordingBusy}
        className={`flex h-8 w-6 shrink-0 items-center justify-center rounded-lg opacity-40 transition-colors ${hover} hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-20`}
        aria-label="下一版动画"><ChevronRight size={14} /></button>
    </div>
  )
}
